/*
 * Blackstone Unlimited reviews (ported from app-gadget-downpour
 * tools/storefront-preview/src; keep in step) — storefront widget core (<unlimited-reviews>).
 *
 * Source of assets/component-reviews.js (minified by
 * tools/storefront-preview/build.mjs — edit here, then `npm run build`).
 *
 * Progressive enhancement over the block's server-rendered markup: summary refresh, the
 * review list, sorting, paging and each review's action row (helpful count + report), and the
 * reading-format switch (ebook / print variants have no narration: see blocks/reviews.liquid).
 * Everything that needs a signed-in customer (own-review panel, composer, voting and
 * reporting) lives in component-reviews-composer.js, which guests never download.
 * Review content is only ever written with textContent / attributes — never parsed as HTML.
 *
 * Members prefixed with "_" are internal: the build mangles them (consistently across both
 * bundles) to keep this file under theme check's 10,000-byte app-block limit.
 */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('unlimited-reviews')) return;

  let composerLoad = null;

  // h('p', { class: 'x', text: 'y' }, [child]) — text goes through textContent only.
  const h = (tag, attrs, kids) => {
    const el = document.createElement(tag);
    for (const k in attrs || {}) {
      const v = attrs[k];
      if (v == null || v === false) continue;
      if (k === 'text') el.textContent = v;
      else if (k === 'class') el.className = v;
      else el.setAttribute(k, v === true ? '' : v);
    }
    for (const c of kids || []) if (c) el.append(c);
    return el;
  };
  const fill = (tpl, vars) =>
    String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  const pct = (v) => Math.max(0, Math.min(100, Math.round((Number(v) || 0) * 100)));
  const num = (v) => Math.max(0, Number(v) || 0);
  const isPublished = (o) => !!o && (o.status || 'published') === 'published';
  const reducedMotion = () =>
    !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);

  class UnlimitedReviews extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const d = this.dataset;
      this.cfg = {
        endpoint: d.endpoint,
        loginUrl: d.loginUrl,
        // The block's range setting keeps this 5–25; the server clamps it too.
        pageSize: +d.pageSize || 10,
        narration: d.showNarration === 'true',
        verified: d.showVerified === 'true',
        source: d.showSource === 'true',
      };
      this._loggedIn = d.loggedIn === 'true';
      let locale;
      try {
        locale = Intl.getCanonicalLocales(d.locale);
      } catch (e) {
        // Unknown tag: fall back to the browser's locale.
      }
      this.nf = new Intl.NumberFormat(locale);
      this._df = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
      this._pr = new Intl.PluralRules(locale);
      this.t = {};
      this._icons = {};
      for (const n of this._q('i18n').content.querySelectorAll('[data-k]'))
        this.t[n.dataset.k] = n.textContent.trim();
      for (const s of this._q('icons').content.querySelectorAll('svg'))
        this._icons[s.dataset.icon] = s;

      this._state = { _sort: 'helpful', _page: 0, _pages: 0, _retryReset: true };
      this._seq = 0;
      this._dialog = this._q('dialog');

      this.addEventListener('click', (e) => this._onClick(e));
      this.addEventListener('change', (e) => this._composer && this._composerChange(e.target));
      // The theme's variant picker: ebook / print variants hide narration (here and in the badge).
      // Other products' pickers (quick buy) fire it too: only this product's variants count.
      document.addEventListener('variant:changed', (e) => {
        const id = `,${e.detail?.variant?.id},`;
        if (d.variants.includes(id))
          for (const el of document.querySelectorAll(`[data-dr-product="${d.drProduct}"]`))
            el.toggleAttribute('data-dr-read', d.readVariants.includes(id));
      });
      this.addEventListener('input', (e) => this._composer && this._composerInput(e));
      // Signed in: warm the signed-in bundle while the browser is idle so actions are instant.
      if (this._loggedIn)
        (window.requestIdleCallback || setTimeout)(() => this._loadComposer().catch(() => {}));
      this._load(true);
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
    }

    _q(name, scope) {
      return (scope || this).querySelector(`[data-dr-${name}]`);
    }

    _icon(name) {
      const svg = this._icons[name];
      return svg ? svg.cloneNode(true) : '';
    }

    _count(n, one, other, vars) {
      return fill(this._pr.select(n) === 'one' ? one : other, { n: this.nf.format(n), ...vars });
    }

    _onClick(e) {
      const b = e.target.closest('button');
      if (!b || !this.contains(b)) return;
      const ds = b.dataset;
      if ('drOpen' in ds) this._openComposer(b);
      else if (ds.drSort) this._setSort(ds.drSort);
      else if ('drMore' in ds) this._load(false);
      else if ('drRetry' in ds) this._load(this._state._retryReset);
      else if ('drExpand' in ds) this._toggleExpand(b);
      else if (this._loggedIn)
        this._loadComposer().then(
          () => this._composerClick(b),
          () => {},
        );
    }

    // ------------------------------------------------------------------ data

    async _request(method, params, body, signal, path) {
      const url = new URL(path || this.cfg.endpoint, location.href);
      for (const k in params || {}) url.searchParams.set(k, params[k]);
      const headers = { Accept: 'application/json' };
      if (body) headers['Content-Type'] = 'application/json';
      const res = await fetch(url, {
        method,
        signal,
        headers,
        body: body && JSON.stringify(body),
      });
      let data = null;
      try {
        data = await res.json();
      } catch (err) {
        // Proxies can answer with an HTML error page.
      }
      return { ok: res.ok && !!data && data.ok !== false, status: res.status, data };
    }

    async _load(reset) {
      if (this._ctrl) this._ctrl.abort();
      const ctrl = (this._ctrl = new AbortController());
      const st = this._state;
      const list = this._q('list');
      const more = this._q('more');
      const page = reset ? 1 : st._page + 1;
      list.setAttribute('aria-busy', 'true');
      // Dim only user-triggered reloads, not the first fetch that hydrates server-rendered reviews.
      if (reset && st._page && list.querySelector('[data-review-id]'))
        list.classList.add('is-stale');
      this._q('list-error').hidden = true;
      more.disabled = true;

      let res;
      try {
        res = await this._request(
          'GET',
          { page, pageSize: this.cfg.pageSize, sort: st._sort },
          null,
          ctrl.signal,
        );
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        res = { status: 0 };
      }
      if (ctrl !== this._ctrl) return;
      list.removeAttribute('aria-busy');
      list.classList.remove('is-stale');
      more.disabled = false;
      if (!res.ok) return this._showListError(res, reset);

      const data = res.data;
      if (data.viewer) this._applyViewer(data.viewer);
      if (data.summary) this._renderSummary(data.summary);

      const own = this._own;
      const items = (data.reviews || []).filter((r) => !(own && (r.isOwn || r.id === own.id)));
      // Replacing in one task means no paint in between: server-rendered items swap for live ones
      // without a flash or duplicates. Keep focus on the same review if it was inside the list.
      const focused =
        reset &&
        list.contains(document.activeElement) &&
        document.activeElement.closest('[data-review-id]');
      if (reset) list.replaceChildren();
      const added = items.map((r) => this._renderReview(r));
      list.append(...added);
      const same = focused && added.find((li) => li.dataset.reviewId === focused.dataset.reviewId);
      if (same) same.firstChild.focus({ preventScroll: true });
      this._fitClamp(list);

      const p = data.page || {};
      st._page = p.page || page;
      st._pages = p.totalPages || 0;
      const total = num(p.total);
      more.hidden = st._page >= st._pages;

      const ownShown = isPublished(own) && (own.title || own.detail);
      this._q('empty').hidden = !(reset && !items.length && !ownShown);
      this._q('status').textContent = total
        ? this._count(total, this.t.countOne, this.t.countOther, {
            shown: this.nf.format(Math.min(total, st._page * (p.pageSize || this.cfg.pageSize))),
          })
        : '';

      // "Show more": move focus to the first newly loaded review.
      if (!reset && added[0]) added[0].firstChild.focus({ preventScroll: reducedMotion() });
    }

    _showListError(res, reset) {
      for (const n of this.querySelectorAll('.dr-review--skeleton')) n.remove();
      const s = res.status;
      let msg = this.t.listError;
      if (s === 404) msg = this.t.listNotFound;
      else if (s === 429) msg = this.t.errRateLimited;
      else if (!s && navigator.onLine === false) msg = this.t.errOffline;
      this._q('list-error-text').textContent = msg;
      this._q('retry').hidden = s === 404;
      this._state._retryReset = reset;
      this._q('list-error').hidden = false;
      this._q('status').textContent = '';
    }

    _setSort(sort) {
      if (sort === this._state._sort) return;
      this._state._sort = sort;
      for (const b of this.querySelectorAll('[data-dr-sort]'))
        b.setAttribute('aria-pressed', String(b.dataset.drSort === sort));
      this._load(true);
    }

    // --------------------------------------------------------------- summary

    // s is the contract's Summary (kept raw; the signed-in bundle normalises it when adjusting).
    _renderSummary(s) {
      this._summary = s;
      const t = this.t;
      const ratings = num(s.ratings);
      const reviews = num(s.reviews);
      const like = pct(s.likePercent);
      const read = this.hasAttribute('data-dr-read');
      this._q('stats').hidden = !ratings;
      this._q('summary-empty').hidden = !!ratings;
      this._q('like-pct').textContent = like;
      this._setBar('overall', like, true);
      this._setBar(
        'narration',
        pct(s.narrationLikePercent),
        num(s.narrationUpCount) + num(s.narrationDownCount) > 0,
      );
      this._q('basis').textContent = this._count(ratings, t.basisOne, t.basisOther);
      this._q('empty-text').textContent = read
        ? this._count(ratings, t.emptyReadOne, t.emptyReadOther)
        : this._count(ratings, t.emptyOne, t.emptyOther);
      this._q('count').textContent = this._count(reviews, t.reviewsOne, t.reviewsOther);
      this._q('toolbar').hidden = reviews < 2;
    }

    _setBar(name, value, visible) {
      const bar = this.querySelector(`[data-dr-bar="${name}"]`);
      if (!bar) return;
      bar.hidden = !visible;
      bar.style.setProperty('--dr-pct', `${value}%`);
      this._q('bar-value', bar).textContent = `${value}%`;
    }

    // ---------------------------------------------------------------- viewer

    _applyViewer(v) {
      if (v.loggedIn === false) this._loggedIn = false;
      this._own = (this._loggedIn && v.ownReview) || null;
      this._blocked = v.reason === 'blocked';
      this._updateCta();
      const slot = this._q('own');
      if (!this._own) {
        slot.hidden = true;
        slot.replaceChildren();
      } else {
        this._loadComposer().then(
          () => this._renderOwn(),
          () => {},
        );
      }
    }

    _canWrite() {
      return this._loggedIn && !!this._dialog && !this._blocked;
    }

    // Every write entry point (summary card, "no written reviews" card, one-tap ratings) and
    // its signed-out twin.
    _updateCta() {
      for (const el of this.querySelectorAll('[data-dr-write]')) el.hidden = !this._canWrite();
      for (const el of this.querySelectorAll('[data-dr-sign-in]')) el.hidden = this._loggedIn;
      const label = this._q('cta-label');
      if (label) label.textContent = this._own ? this.t.update : this.t.write;
      this._q('blocked-note').hidden = !(this._loggedIn && this._blocked);
    }

    _loadComposer() {
      if (this._composer) return Promise.resolve();
      if (!composerLoad) {
        composerLoad = new Promise((resolve, reject) => {
          const s = h('script', { src: this.dataset.composerSrc, async: true });
          s.onload = resolve;
          s.onerror = () => {
            composerLoad = null;
            reject(new Error('composer'));
          };
          document.head.append(s);
        });
      }
      return composerLoad;
    }

    _openComposer(opener) {
      if (!this._canWrite()) return;
      const note = this._q('cta-error');
      note.hidden = true;
      this._loadComposer().then(
        () => this._openForm(opener),
        () => {
          note.textContent = navigator.onLine === false ? this.t.errOffline : this.t.errGeneric;
          note.hidden = false;
        },
      );
    }

    // ---------------------------------------------------------------- review

    _pill(kind, thumb) {
      if (thumb !== 'up' && thumb !== 'down') return null;
      const up = thumb === 'up';
      return h(
        'span',
        {
          class: `dr-pill dr-pill--${thumb} dr-pill--${kind}`,
          'data-testid': `dr-review-thumb-${kind}`,
          'data-thumb': thumb,
        },
        [
          this._icon(up ? 'thumb-up' : 'thumb-down'),
          h('span', { text: this.t[kind] }),
          h('span', { class: 'dr-vh', text: up ? this.t.thumbUp : this.t.thumbDown }),
        ],
      );
    }

    // The non-interactive "Reported" state (also used after a successful report).
    _reported() {
      return h(
        'span',
        { class: 'dr-act dr-act--done', 'data-testid': 'dr-reported', tabindex: '-1' },
        [this._icon('flag'), h('span', { text: this.t.reported })],
      );
    }

    // "Helpful · 12" + "Report". Signed in: buttons (handled by the signed-in bundle);
    // guests: the same visuals as sign-in links, so the count still shows.
    _actions(r) {
      const t = this.t;
      const n = num(r.helpfulCount);
      const login = this.cfg.loginUrl;
      const helpful = [
        this._icon('thumb-up-outline'),
        h('span', { text: t.helpful }),
        h('span', {
          class: 'dr-act__count',
          'data-testid': 'dr-helpful-count',
          'data-n': n,
          hidden: !n,
          text: this.nf.format(n),
        }),
      ];
      const report = [this._icon('flag'), h('span', { text: t.report })];
      return h(
        'div',
        { class: 'dr-review__actions' },
        this._loggedIn
          ? [
              h(
                'button',
                {
                  type: 'button',
                  class: 'dr-act',
                  'data-dr-helpful': '',
                  'data-testid': 'dr-helpful',
                  'aria-pressed': String(!!r.viewerFoundHelpful),
                },
                helpful,
              ),
              r.viewerReported
                ? this._reported()
                : h(
                    'button',
                    {
                      type: 'button',
                      class: 'dr-act dr-act--quiet',
                      'data-dr-report': '',
                      'data-testid': 'dr-report',
                      'aria-haspopup': 'dialog',
                    },
                    report,
                  ),
            ]
          : [
              h(
                'a',
                {
                  class: 'dr-act',
                  href: login,
                  'data-testid': 'dr-helpful-sign-in',
                  'aria-label': fill(t.helpfulGuest, { n: this.nf.format(n) }),
                },
                helpful,
              ),
              h(
                'a',
                {
                  class: 'dr-act dr-act--quiet',
                  href: login,
                  'data-testid': 'dr-report-sign-in',
                  'aria-label': t.reportGuest,
                },
                report,
              ),
            ],
      );
    }

    // own = true renders the signed-in customer's review card (inside the own panel).
    _renderReview(r, own) {
      const id = `${this.id || 'dr'}-r${++this._seq}`;
      const t = this.t;
      const anon = !r.nickname;
      const date = new Date(r.createdAt);
      const meta = [
        !own &&
          this.cfg.verified &&
          r.verifiedListener &&
          h('span', { class: 'dr-meta dr-meta--verified', 'data-testid': 'dr-review-verified' }, [
            this._icon('verified'),
            h('span', { text: t.verified }),
          ]),
        !own &&
          this.cfg.source &&
          r.source === 'app' &&
          h('span', { class: 'dr-meta', 'data-testid': 'dr-review-source' }, [
            this._icon('phone'),
            h('span', { text: t.sourceApp }),
          ]),
      ].filter(Boolean);
      const initial = anon ? '' : [...r.nickname][0].toUpperCase();
      const pills = [
        this._pill('overall', r.overallThumb),
        this.cfg.narration && this._pill('narration', r.narrationThumb),
      ].filter(Boolean);
      // Avatar colour: the initial's place in the alphabet modulo 6 (-1, the default colour,
      // for anything else), as in snippets/dr-review.liquid.
      const kids = [
        h('div', { class: 'dr-review__head' }, [
          anon
            ? h('span', { class: 'dr-avatar dr-avatar--anon' }, [this._icon('person')])
            : h('span', {
                class: `dr-avatar dr-avatar--${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.indexOf(initial) % 6}`,
                'aria-hidden': 'true',
                text: initial,
              }),
          h('div', { class: 'dr-review__who' }, [
            h('span', {
              class: 'dr-review__name',
              id,
              'data-testid': 'dr-review-name',
              text: anon ? t.anonymous : r.nickname,
            }),
            r.isOwn &&
              !own &&
              h('span', {
                class: 'dr-own-badge',
                'data-testid': 'dr-review-own-badge',
                text: t.yourReview,
              }),
            !isNaN(date) &&
              h('time', {
                class: 'dr-review__date',
                datetime: r.createdAt,
                text: this._df.format(date),
              }),
          ]),
          pills.length && h('div', { class: 'dr-review__pills' }, pills),
        ]),
        meta.length && h('div', { class: 'dr-review__meta' }, meta),
        r.title &&
          h('h3', { class: 'dr-review__title', 'data-testid': 'dr-review-title', text: r.title }),
      ];
      // Clamped to three lines with "Read more"; _fitClamp drops both where the text fits.
      if (r.detail)
        kids.push(
          h('p', {
            class: 'dr-review__detail is-clamped',
            id: `${id}-d`,
            'data-testid': 'dr-review-detail',
            text: r.detail,
          }),
          h('button', {
            type: 'button',
            class: 'dr-link',
            'data-dr-expand': '',
            'aria-expanded': 'false',
            'aria-controls': `${id}-d`,
            text: t.readMore,
          }),
        );
      if (!own && !r.isOwn) kids.push(this._actions(r));
      return h(
        own ? 'div' : 'li',
        {
          class: own ? 'dr-review dr-review--own' : 'dr-review',
          'data-testid': own ? 'dr-own-card' : 'dr-review',
          'data-review-id': r.id || '',
        },
        [h('article', { class: 'dr-review__body', 'aria-labelledby': id, tabindex: '-1' }, kids)],
      );
    }

    // "Read more" only where the clamp actually hides something at this width (call once the
    // reviews are in the document).
    _fitClamp(scope) {
      for (const p of scope.querySelectorAll('.is-clamped'))
        if (p.scrollHeight <= p.clientHeight + 1) {
          p.classList.remove('is-clamped');
          p.nextSibling.hidden = true;
        }
    }

    // The detail paragraph always sits just before its "Read more" button.
    _toggleExpand(btn) {
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? this.t.readLess : this.t.readMore;
      btn.previousSibling.classList.toggle('is-clamped', !open);
    }
  }

  // Shared with component-reviews-composer.js.
  Object.assign(UnlimitedReviews, { h, fill, num, isPublished, reducedMotion });
  customElements.define('unlimited-reviews', UnlimitedReviews);
})();
